import { Check, Minus, X, type LucideIcon } from 'lucide-react';
import { useTranslation } from '../contexts/TranslationContext';
import { AI_RELIABILITY_PATH, localizePublicHref } from '../utils/publicRoutes';
import { ArrowRight } from './Icons';
import { LANDING_SECTION_TITLE_CLASS_NAME, landingSectionClassName } from './landingSectionClassName';
import { BrandLink } from './ui/BrandButton';

const SCENARIOS = ['palen', 'withoutPalen'] as const;
const COMPARISON_ROWS = [
  { criterion: 'expertise', palen: 'advantage', withoutPalen: 'medium' },
  { criterion: 'sources', palen: 'advantage', withoutPalen: 'limit' },
  { criterion: 'timeSavings', palen: 'advantage', withoutPalen: 'medium' },
  { criterion: 'currentRegulations', palen: 'advantage', withoutPalen: 'limit' },
  { criterion: 'hallucinationResistance', palen: 'advantage', withoutPalen: 'limit' },
  { criterion: 'confidence', palen: 'advantage', withoutPalen: 'limit' },
] as const;

type ComparisonStatus = 'advantage' | 'medium' | 'limit';

const STATUS_PRESENTATION = {
  advantage: {
    icon: Check,
    className: 'theme-comparison-check',
  },
  medium: {
    icon: Minus,
    className: 'text-[rgb(var(--theme-highlight-strong))]',
  },
  limit: {
    icon: X,
    className: 'theme-danger-icon',
  },
} as const satisfies Record<ComparisonStatus, {
  icon: LucideIcon;
  className: string;
}>;

export function AdrApproachComparison() {
  const { language, t } = useTranslation();

  return (
    <section
      id="adr-comparison"
      aria-labelledby="adr-comparison-title"
      className={landingSectionClassName({
        tone: 'soft',
        className: 'overflow-hidden px-3 py-20 sm:px-6 sm:py-28',
        fillAvailable: false,
      })}
    >
      <div className="mx-auto w-full max-w-6xl">
        <header className="mx-auto max-w-4xl text-center">
          <h2
            id="adr-comparison-title"
            className={`${LANDING_SECTION_TITLE_CLASS_NAME} break-words !text-3xl sm:!text-4xl md:!text-5xl`}
          >
            {t('adrComparison.title')}
          </h2>
        </header>

        <div className="theme-comparison-shell mx-auto mt-8 max-w-5xl overflow-hidden rounded-xl border border-[rgb(var(--theme-border)/0.72)] sm:mt-10 sm:rounded-2xl">
          <table className="w-full table-fixed border-collapse">
            <caption className="sr-only">{t('adrComparison.caption')}</caption>
            <thead>
              <tr className="border-b border-[rgb(var(--theme-border)/0.72)]">
                <th
                  scope="col"
                  className="theme-comparison-heading-cell w-[50%] px-3 py-4 text-left text-xs font-extrabold uppercase tracking-[0.08em] text-[rgb(var(--theme-text))] sm:w-[42%] sm:px-6 sm:py-5 sm:text-sm sm:tracking-[0.1em]"
                >
                  <span className="sr-only">{t('adrComparison.criteriaLabel')}</span>
                </th>
                {SCENARIOS.map((scenario) => (
                  <th
                    key={scenario}
                    scope="col"
                    data-adr-approach={scenario}
                    className={`break-words border-l border-[rgb(var(--theme-border)/0.48)] px-[3px] py-4 text-center text-xs font-extrabold leading-4 sm:px-4 sm:py-5 sm:text-base sm:leading-6 ${
                      scenario === 'palen' ? 'theme-comparison-palen-cell' : 'theme-comparison-heading-cell text-[rgb(var(--theme-text))]'
                    }`}
                  >
                    {t(`adrComparison.scenarios.${scenario}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map(({ criterion, ...statuses }) => (
                <tr key={criterion} className="theme-comparison-row border-b border-[rgb(var(--theme-border)/0.56)] last:border-b-0">
                  <th
                    scope="row"
                    className="theme-comparison-criterion break-words px-3 py-4 text-left text-sm font-bold leading-5 text-[rgb(var(--theme-text))] sm:px-6 sm:py-5 sm:text-base sm:leading-6"
                  >
                    {t(`adrComparison.criteria.${criterion}`)}
                  </th>
                  {SCENARIOS.map((scenario) => (
                    <td
                      key={scenario}
                      className={`border-l border-[rgb(var(--theme-border)/0.4)] px-[3px] py-3.5 text-center sm:px-4 sm:py-4 ${
                        scenario === 'palen' ? 'theme-comparison-palen-cell' : 'theme-comparison-neutral-cell'
                      }`}
                    >
                      <ComparisonStatusIcon status={statuses[scenario]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mx-auto mb-16 mt-7 flex w-full max-w-sm justify-center px-2 sm:mb-0 sm:mt-8 sm:max-w-none sm:px-0">
          <BrandLink
            href={localizePublicHref(AI_RELIABILITY_PATH, language)}
            data-adr-comparison-cta="true"
            className="w-full min-w-0 max-w-[20rem] gap-2 whitespace-normal px-4 text-sm leading-5 sm:w-auto sm:max-w-none sm:px-5 sm:text-[0.95rem]"
          >
            <span className="min-w-0 break-words text-center">{t('impact.reliabilityLink')}</span>
            <ArrowRight className="h-4 w-4 shrink-0 sm:h-5 sm:w-5" aria-hidden="true" />
          </BrandLink>
        </div>
      </div>
    </section>
  );
}

function ComparisonStatusIcon({ status }: { status: ComparisonStatus }) {
  const { t } = useTranslation();
  const { icon: Icon, className } = STATUS_PRESENTATION[status];
  const label = t(`adrComparison.status.${status}`);

  return (
    <span
      data-comparison-status={status}
      className={`mx-auto inline-flex h-[32px] w-[32px] items-center justify-center sm:h-9 sm:w-9 ${className}`}
    >
      <span className="sr-only">{label}</span>
      <Icon className="h-[22px] w-[22px] stroke-[2.75] sm:h-6 sm:w-6" aria-hidden="true" />
    </span>
  );
}
